import React from 'react';
import { fpBackButton, fpCard, fpHeader, fpHeaderTitle, fpSticker, fpStickerSmall } from '../utils/fpUi';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen } from 'lucide-react';
import { comics } from '../data/comics';
import { useEntitlements } from '../hooks/useEntitlements';
import { EncryptedImage } from '../components/EncryptedImage';
import { useLanguage } from '../contexts/LanguageContext';
import { useDarkMode } from '../contexts/DarkModeContext';

export default function MyCollection() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isDarkMode } = useDarkMode();
  const { unlockedComicIds } = useEntitlements();

  const myComics = comics.filter(comic => unlockedComicIds.includes(comic.id));

  const handleComicClick = (comicId) => {
    navigate(`/comic/${comicId}`);
  };

  return (
    <div style={{
      ...styles.container,
      ...(isDarkMode ? styles.containerDark : {}),
    }}>
      <div style={{
        ...styles.header,
        ...(isDarkMode ? styles.headerDark : {}),
      }}>
        <button onClick={() => navigate('/')} style={{
          ...styles.backButton,
          ...(isDarkMode ? styles.backButtonDark : {}),
        }}>
          <ArrowLeft size={20} color={isDarkMode ? "#000" : "#fff"} />
        </button>
        <h2 style={styles.title}>{t('myCollection')}</h2>
        <div style={styles.spacer} />
      </div>
      
      <div style={styles.content}>
        {myComics.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>📚</div>
            <h3 style={styles.emptyTitle}>{t('noComicsYet')}</h3>
            <p style={styles.emptyText}>
              {t('noComicsDescription')}
            </p>
            <button onClick={() => navigate('/')} style={styles.browseButton}>
              <BookOpen size={20} color="#fff" />
              <span>{t('browseComics')}</span>
            </button>
          </div>
        ) : (
          <>
            <div style={styles.collectionHeader}>
              <h3 style={{
                ...styles.collectionTitle,
                ...(isDarkMode ? styles.collectionTitleDark : {}),
              }}>{t('yourUnlockedComics')}</h3>
              <p style={styles.collectionCount}>{myComics.length} {t('comicsInCollection')}</p>
            </div>
            <div style={styles.comicsGrid}>
              {myComics.map((comic) => (
                <div
                  key={comic.id}
                  style={{
                    ...styles.comicCard,
                    ...(isDarkMode ? styles.comicCardDark : {}),
                  }}
                  onClick={() => handleComicClick(comic.id)}
                >
                  <div style={styles.coverContainer}>
                    <EncryptedImage
                      src={comic.coverUrl}
                      alt={comic.title}
                      style={styles.comicCover}
                    />
                    <div style={styles.pageCount}>
                      {comic.pages.length} {t('pages')}
                    </div>
                  </div>
                  <h4 style={{
                    ...styles.comicTitle,
                    ...(isDarkMode ? styles.comicTitleDark : {}),
                  }}>{comic.title}</h4>
                  <button style={styles.readButton}>
                    {t('readNow')}
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundImage: 'url(/wallpaper.png)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed',
    display: 'flex',
    flexDirection: 'column',
  },
  containerDark: {
    backgroundImage: 'none',
    backgroundColor: '#000000',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    backgroundColor: '#1a1a2e',
    color: 'white',
    flexShrink: 0,
    boxShadow: '0 2px 8px rgba(255, 20, 147, 0.3)',
    ...fpHeader,
  },
  headerDark: {
    backgroundColor: '#000000',
    borderBottom: '1px solid #333',
    boxShadow: 'none',
  },
  backButton: {
    background: 'rgba(255, 255, 255, 0.2)',
    border: 'none',
    cursor: 'pointer',
    padding: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backdropFilter: 'blur(10px)',
    transition: 'background-color 0.2s',
    ...fpBackButton,
  },
  backButtonDark: {
    background: '#ffffff',
  },
  title: {
    fontSize: '20px',
    fontWeight: 'bold',
    margin: 0,
    textAlign: 'center',
    ...fpHeaderTitle,
  },
  spacer: {
    width: '44px',
  },
  content: {
    flex: 1,
    padding: '20px',
    overflowY: 'auto',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    textAlign: 'center',
    padding: '20px',
  },
  emptyIcon: {
    fontSize: '80px',
    marginBottom: '20px',
  },
  emptyTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#fff',
    margin: '0 0 12px 0',
    textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
  },
  emptyText: {
    fontSize: '16px',
    color: '#ccc',
    maxWidth: '300px',
    lineHeight: '1.5',
    marginBottom: '30px',
  },
  browseButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '14px 28px',
    background: 'linear-gradient(135deg, #FF1493 0%, #FF69B4 100%)',
    color: 'white',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '25px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(255, 20, 147, 0.4)',
    transition: 'transform 0.2s',
    ...fpSticker,
  },
  collectionHeader: {
    textAlign: 'center',
    marginBottom: '24px',
  },
  collectionTitle: {
    fontSize: '22px',
    fontWeight: 'bold',
    color: '#FFD700',
    margin: '0 0 8px 0',
    textShadow: '0 0 10px rgba(255, 215, 0, 0.5)',
  },
  collectionTitleDark: {
    textShadow: 'none',
  },
  collectionCount: {
    fontSize: '14px',
    color: '#ccc',
    margin: 0,
  },
  comicsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: '20px',
    justifyItems: 'center',
  },
  comicCard: {
    width: '100%',
    maxWidth: '180px',
    backgroundColor: 'white',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 4px 16px rgba(255, 20, 147, 0.3)',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    ...fpCard,
  },
  comicCardDark: {
    backgroundColor: '#111111',
    border: '1px solid #333',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.5)',
  },
  coverContainer: {
    position: 'relative',
    width: '100%',
  },
  comicCover: {
    width: '100%',
    aspectRatio: '2/3',
    objectFit: 'cover',
    display: 'block',
  },
  pageCount: {
    position: 'absolute',
    bottom: '8px',
    right: '8px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: 'white',
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '600',
  },
  comicTitle: {
    padding: '12px 12px 8px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#333',
    margin: 0,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  comicTitleDark: {
    color: '#ffffff',
  },
  readButton: {
    width: 'calc(100% - 24px)',
    margin: '0 12px 12px',
    padding: '10px',
    background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
    color: '#000',
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    boxShadow: '0 2px 8px rgba(255, 215, 0, 0.3)',
    ...fpStickerSmall,
  },
};
